import {
  DndContext,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ActivityCard } from "../../../components/card/activityCard";

// Pixel height of one 15-minute segment. Everything on the timeline is a
// multiple of this, so an activity's height maps directly to its duration.
// Tall enough that a 15-min card fits with its top/bottom padding.
const QUARTER_H = 46;
const GUTTER = "w-16"; // time-label gutter; rules + cards start after it

function timeToMinutes(t) {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + m;
}

// Minutes-of-day -> "HH:mm" (24h), how activity start/end are stored.
function minutesToTime(min) {
  const norm = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

// Minutes-of-day -> "HH:MM" on a 24-hour clock (wraps past midnight for
// overnight shifts).
function formatTimeLabel(min) {
  const norm = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

// Assign overlapping activities to side-by-side lanes. Activities that don't
// overlap in time share a lane; a cluster of mutually-overlapping activities is
// split into equal-width columns (like the schedule table's multi-shift cells).
function assignLanes(items) {
  const sorted = [...items].sort(
    (a, b) => a.startAbs - b.startAbs || a.endAbs - b.endAbs,
  );
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const colEnds = []; // last endAbs placed in each column
    cluster.forEach((ev) => {
      let placed = false;
      for (let c = 0; c < colEnds.length; c++) {
        if (colEnds[c] <= ev.startAbs) {
          ev.col = c;
          colEnds[c] = ev.endAbs;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.col = colEnds.length;
        colEnds.push(ev.endAbs);
      }
    });
    cluster.forEach((ev) => (ev.numCols = colEnds.length));
    cluster = [];
    clusterEnd = -Infinity;
  };

  sorted.forEach((ev) => {
    if (cluster.length && ev.startAbs >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endAbs);
  });
  flush();
  return sorted;
}

// While dragging, lock to the vertical axis and snap to 15-min steps so the
// block previews on the same grid the times land on.
const snapVertical = ({ transform }) => ({
  ...transform,
  x: 0,
  y: Math.round(transform.y / QUARTER_H) * QUARTER_H,
});

// A timed activity rendered as a draggable block. Dragging it changes its start
// time (duration preserved); the drop handler clamps it to the shift bounds.
function TimedActivityBlock({
  id,
  top,
  height,
  leftPct,
  widthPct,
  canChange,
  children,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id, disabled: !canChange });
  return (
    <div
      ref={setNodeRef}
      className={`absolute pl-1.5 py-1.5 pr-0.5 pointer-events-auto ${
        canChange ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      style={{
        top,
        height,
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        transform: transform
          ? `translate3d(0, ${transform.y}px, 0)`
          : undefined,
        zIndex: isDragging ? 30 : undefined,
        opacity: isDragging ? 0.9 : undefined,
      }}
      {...(canChange ? { ...attributes, ...listeners } : {})}
    >
      {children}
    </div>
  );
}

function ActivitiesList({
  activitySlots,
  getActivitiesForSlot,
  openAddModal,
  openEditModal,
  selectedActivityId,
  onDeleteActivity,
  canChange = true,
  activities = [],
  shiftStartTime,
  shiftEndTime,
  onUpdateActivityTime,
}) {
  const adHocSlot = activitySlots.find((s) => s.isAdHoc);
  const adHocActivities = adHocSlot ? getActivitiesForSlot(adHocSlot) : [];

  // ----- Timeline geometry -----
  const startMins = timeToMinutes(shiftStartTime);
  let endMins = timeToMinutes(shiftEndTime);
  if (endMins <= startMins) endMins += 1440; // overnight shift
  const totalMins = endMins - startMins;
  const segments = Math.max(0, Math.round(totalMins / 15));
  const gridHeight = segments * QUARTER_H;

  // Timed activities positioned by start/duration, then split into lanes.
  const timed = activities
    .filter((a) => !a.adHoc && a.start)
    .map((a) => {
      let s = timeToMinutes(a.start);
      if (s < startMins) s += 1440;
      let e = a.end ? timeToMinutes(a.end) : s + 15;
      if (e <= s) e += 1440;
      // Keep blocks inside the shift bounds.
      s = Math.max(s, startMins);
      e = Math.min(e, endMins);
      return { activity: a, startAbs: s, endAbs: Math.max(e, s + 15) };
    });
  const laid = assignLanes(timed);

  // A small movement threshold so a plain click still opens the edit sheet.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = ({ active, delta }) => {
    if (!onUpdateActivityTime || !delta) return;
    const steps = Math.round(delta.y / QUARTER_H);
    if (!steps) return;
    const item = laid.find((l) => l.activity.id === active.id);
    if (!item) return;
    const duration = item.endAbs - item.startAbs;
    // Clamp so the activity stays within the shift start/end.
    const newStart = Math.max(
      startMins,
      Math.min(item.startAbs + steps * 15, endMins - duration),
    );
    if (newStart === item.startAbs) return;
    onUpdateActivityTime(
      item.activity.id,
      minutesToTime(newStart),
      minutesToTime(newStart + duration),
    );
  };

  return (
    <div className="overflow-hidden">
      {/* Ad hoc activities (no fixed time). Same click-to-add section as the
          timed rows: click the highlighted area to add. */}
      {adHocSlot && (
        <div className="flex items-stretch" style={{ minHeight: QUARTER_H }}>
          <div
            className={`pr-2 ${GUTTER} shrink-0 text-xs text-gray-700 dark:text-gray-300 text-right whitespace-nowrap`}
          >
            {adHocSlot.label}
          </div>
          <div className="relative flex-1">
            {/* Clickable add background — same as a timed section. */}
            <div
              className={`absolute inset-0 ${
                canChange
                  ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-black"
                  : ""
              }`}
              onClick={canChange ? () => openAddModal(adHocSlot) : undefined}
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gray-200 dark:bg-gray-600" />
            </div>
            {/* Ad hoc cards overlay the section (click-through except on the
                cards) so an item fills the space instead of stacking below a
                separate empty band — same behaviour as the timed rows. */}
            {adHocActivities.length > 0 && (
              <div className="relative pointer-events-none pl-1.5 pr-2 py-1.5 space-y-1.5">
                {adHocActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="pointer-events-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActivityCard
                      activity={activity}
                      isSelected={selectedActivityId === activity.id}
                      onClick={
                        canChange ? () => openEditModal(activity) : undefined
                      }
                      showDelete={canChange}
                      onDelete={canChange ? onDeleteActivity : undefined}
                      fillHeight
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline: click a 15-min section to add; activities sit on top,
          positioned by time (Google Calendar style). */}
      {segments > 0 && (
        <DndContext
          sensors={sensors}
          modifiers={[snapVertical]}
          onDragEnd={handleDragEnd}
        >
          <div className="relative" style={{ height: gridHeight + 18 }}>
          {/* Clickable 15-min sections. The area right of the label gutter
              highlights on hover and adds an activity at that time on click. */}
          {Array.from({ length: segments }).map((_, i) => {
            const minute = startMins + i * 15;
            const onHour = minute % 60 === 0;
            return (
              <div
                key={i}
                className="absolute inset-x-0 flex items-stretch"
                style={{ top: i * QUARTER_H, height: QUARTER_H }}
              >
                <div
                  className={`pr-2 ${GUTTER} shrink-0 text-right leading-none whitespace-nowrap ${
                    onHour
                      ? "text-xs text-gray-700 dark:text-gray-300"
                      : "text-[10px] text-gray-400 dark:text-gray-500"
                  }`}
                >
                  {formatTimeLabel(minute)}
                </div>
                <div
                  className={`relative flex-1 ${
                    canChange
                      ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-black"
                      : ""
                  }`}
                  onClick={
                    canChange
                      ? () =>
                          openAddModal({
                            label: formatTimeLabel(minute),
                            isAdHoc: false,
                          })
                      : undefined
                  }
                >
                  <div
                    className={`absolute inset-x-0 top-0 h-px ${
                      onHour
                        ? "bg-gray-200 dark:bg-gray-600"
                        : "bg-gray-100 dark:bg-gray-700"
                    }`}
                  />
                </div>
              </div>
            );
          })}

          {/* Closing edge with the shift end time (not clickable) */}
          <div
            className="absolute inset-x-0 flex items-start"
            style={{ top: gridHeight }}
          >
            <div
              className={`pr-2 ${GUTTER} shrink-0 text-right leading-none whitespace-nowrap text-xs text-gray-700 dark:text-gray-300`}
            >
              {formatTimeLabel(endMins)}
            </div>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
          </div>

          {/* Activity blocks — inset on the right so a strip always stays
              clickable for adding a concurrent activity (never fills 100%).
              The layer is click-through except on the cards themselves, so the
              sections underneath stay hoverable/clickable. */}
          <div className="absolute inset-y-0 left-16 right-[8%] pointer-events-none">
            {laid.map(({ activity, startAbs, endAbs, col, numCols }) => {
              const top = ((startAbs - startMins) / 15) * QUARTER_H;
              const height = ((endAbs - startAbs) / 15) * QUARTER_H;
              const width = 100 / (numCols || 1);
              return (
                <TimedActivityBlock
                  key={activity.id}
                  id={activity.id}
                  top={top}
                  height={height}
                  leftPct={col * width}
                  widthPct={width}
                  canChange={canChange}
                >
                  <ActivityCard
                    activity={activity}
                    isSelected={selectedActivityId === activity.id}
                    onClick={
                      canChange ? () => openEditModal(activity) : undefined
                    }
                    showDelete={canChange}
                    onDelete={canChange ? onDeleteActivity : undefined}
                    fillHeight
                  />
                </TimedActivityBlock>
              );
            })}
          </div>
          </div>
        </DndContext>
      )}
    </div>
  );
}

export { ActivitiesList };
